/**
 * usePauseAndGo - PauseAndGo 트리거 감지, 진행 상태 관리, 완료 처리 훅
 */
import { useState, useCallback, useRef, useMemo } from 'react'
import type { PauseAndGoItem, PauseAndGoProgress, PauseAndGoProgressEntry } from './types'

interface UsePauseAndGoOptions {
  pauseAndGos: PauseAndGoItem[]
  /** 현재 재생 중인 번들 아이템 */
  currentBundleItem: 'main' | 'summary'
  onPause: () => void
  onPlay: () => void
  onSeek: (timeMs: number) => void
}

interface UsePauseAndGoReturn {
  /** 현재 활성 PAG (오버레이에 표시) */
  currentPag: PauseAndGoItem | null
  /** 전체 PAG 진행 상태 */
  progress: PauseAndGoProgress
  /** 비디오 onProgress 콜백에서 호출 */
  handleVideoProgress: (currentTimeMs: number) => void
  /** PAG 인터랙션 완료 시 호출 */
  handleComplete: (pagId: number, result: PauseAndGoProgressEntry['result']) => void
  /** 완료 없이 닫기 (예: 건너뛰기) */
  handleDismiss: () => void
  /** 전체 진행 상태 초기화 */
  resetProgress: () => void
  /** 현재 번들 아이템에 해당하는 PAG 목록 */
  filteredPauseAndGos: PauseAndGoItem[]
}

export function usePauseAndGo({
  pauseAndGos,
  currentBundleItem,
  onPause,
  onPlay,
  onSeek,
}: UsePauseAndGoOptions): UsePauseAndGoReturn {
  const [currentPag, setCurrentPag] = useState<PauseAndGoItem | null>(null)
  const [progress, setProgress] = useState<PauseAndGoProgress>({})

  /** 동일 PAG가 한 번의 통과에서 재트리거되지 않도록 방지 */
  const lastTriggeredId = useRef<number | null>(null)

  /** 현재 번들 아이템에 해당하는 PAG 목록 (메모이제이션) */
  const filteredPauseAndGos = useMemo(
    () =>
      pauseAndGos.filter(
        (p) => p.bundleItem == null || p.bundleItem === currentBundleItem
      ),
    [pauseAndGos, currentBundleItem]
  )

  /**
   * 비디오 재생 진행 이벤트 핸들러
   * — currentTimeMs >= p.time && currentTimeMs <= p.time + 1000 범위에서 미완료 PAG 트리거
   */
  const handleVideoProgress = useCallback(
    (currentTimeMs: number) => {
      // 이미 오버레이가 표시 중이면 무시
      if (currentPag !== null) return

      const triggered = filteredPauseAndGos.find((p) => {
        if (progress[p.id]?.completed) return false
        if (lastTriggeredId.current === p.id) return false
        return currentTimeMs >= p.time && currentTimeMs <= p.time + 1000
      })

      if (triggered) {
        lastTriggeredId.current = triggered.id
        onPause()
        setCurrentPag(triggered)
      }
    },
    [currentPag, filteredPauseAndGos, progress, onPause]
  )

  /**
   * PAG 인터랙션 완료 처리
   * — 타입별 시크 로직 적용 후 재생 재개
   */
  const handleComplete = useCallback(
    (pagId: number, result: PauseAndGoProgressEntry['result']) => {
      const pag = pauseAndGos.find((p) => p.id === pagId)

      setProgress((prev) => {
        const entry: PauseAndGoProgressEntry = {
          completed: true,
          result,
          completedAt: Date.now(),
        }

        // 시크가 발생하는 경우 seekedTo 기록
        let seekedTo: number | undefined

        if (pag) {
          switch (pag.type) {
            case 'PAUSE_AND_REPEAT':
              if (result === 'no' && pag.whenToGo != null) {
                seekedTo = pag.whenToGo
              }
              break
            case 'PAUSE_AND_QUIZ':
              if (result === 'incorrect' && pag.whenToGoBack != null) {
                seekedTo = pag.whenToGoBack
              }
              break
            default:
              if (pag.whenToGoBack != null) {
                seekedTo = pag.whenToGoBack
              }
              break
          }
        }

        if (seekedTo != null) {
          entry.seekedTo = seekedTo
        }

        return { ...prev, [pagId]: entry }
      })

      setCurrentPag(null)

      // 시크 후 재생 또는 바로 재생
      if (pag) {
        switch (pag.type) {
          case 'PAUSE_AND_REPEAT':
            if (result === 'no' && pag.whenToGo != null) {
              // lastTriggeredId 초기화 — 되돌아간 구간에서 동일 PAG 재트리거 가능
              lastTriggeredId.current = null
              onSeek(pag.whenToGo)
            }
            onPlay()
            break

          case 'PAUSE_AND_QUIZ':
            if (result === 'incorrect' && pag.whenToGoBack != null) {
              lastTriggeredId.current = null
              onSeek(pag.whenToGoBack)
            }
            onPlay()
            break

          case 'PAUSE_AND_GO':
          case 'PAUSE_AND_AUTOGO':
          case 'PAUSE_AND_VIDEO':
          default:
            if (pag.whenToGoBack != null) {
              lastTriggeredId.current = null
              onSeek(pag.whenToGoBack)
            }
            onPlay()
            break
        }
      } else {
        onPlay()
      }
    },
    [pauseAndGos, onSeek, onPlay]
  )

  /**
   * 완료 처리 없이 PAG 오버레이를 닫고 재생 재개 (건너뛰기)
   */
  const handleDismiss = useCallback(() => {
    setCurrentPag(null)
    onPlay()
  }, [onPlay])

  /**
   * 전체 진행 상태 및 트리거 기록 초기화
   */
  const resetProgress = useCallback(() => {
    setProgress({})
    setCurrentPag(null)
    lastTriggeredId.current = null
  }, [])

  return {
    currentPag,
    progress,
    handleVideoProgress,
    handleComplete,
    handleDismiss,
    resetProgress,
    filteredPauseAndGos,
  }
}

export default usePauseAndGo
